// WelcomeUploader — record a 30-second family welcome (voice + optional photo
// + optional caption) that replaces the AI-generated opening on the demo
// player. Mirrors the in-browser recorder pattern already used by ChapterEditor.
import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, CheckCircle, Camera, Trash, Translate } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const MAX_SECONDS = 30;

export function WelcomeUploader({ profile, onSaved }) {
    const profileId = profile.id;
    const [recording, setRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
    const [caption, setCaption] = useState(profile.welcome_caption || "");
    const [seconds, setSeconds] = useState(0);
    const [busy, setBusy] = useState(false);
    const [translating, setTranslating] = useState(false);
    const recorderRef = useRef(null);
    const timerRef = useRef(null);

    const hasExisting = !!profile.welcome_audio_file_id;
    const translationStatus = profile.welcome_translation_status || "idle";
    const translationCount = Object.keys(profile.welcome_audio_translations || {}).length;
    const isQueuedOrRunning = ["queued", "transcribing", "translating"].includes(translationStatus);

    // Poll profile while a translation job is running so the UI catches up.
    useEffect(() => {
        if (!isQueuedOrRunning) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await api.get(`/profiles/${profileId}`);
                if (cancelled) return;
                if (data.profile && onSaved) onSaved(data.profile);
                if (["ready", "idle"].includes(data.profile?.welcome_translation_status)
                    || (data.profile?.welcome_translation_status || "").startsWith("error")) {
                    return;
                }
                setTimeout(tick, 4000);
            } catch (e) {
                setTimeout(tick, 8000);
            }
        };
        const t = setTimeout(tick, 4000);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isQueuedOrRunning, profileId]);

    async function startRecording() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            const chunks = [];
            mr.ondataavailable = (e) => chunks.push(e.data);
            mr.onstop = () => {
                const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
                setAudioBlob(blob);
                setAudioPreviewUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach((t) => t.stop());
                setRecording(false);
                if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
            };
            mr.start();
            recorderRef.current = mr;
            setRecording(true);
            setSeconds(0);
            timerRef.current = setInterval(() => {
                setSeconds((s) => {
                    const next = s + 1;
                    if (next >= MAX_SECONDS) {
                        // hard stop at 30 seconds
                        if (mr.state !== "inactive") mr.stop();
                    }
                    return next;
                });
            }, 1000);
        } catch (e) {
            toast.error("Microphone access denied. Check your browser permissions.");
        }
    }

    function stopRecording() {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop();
        }
    }

    function pickPhoto(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please choose an image file.");
            return;
        }
        setPhotoFile(f);
        setPhotoPreviewUrl(URL.createObjectURL(f));
    }

    async function uploadBlob(blob, filename, contentType) {
        const fd = new FormData();
        fd.append("file", new File([blob], filename, { type: contentType }));
        const { data } = await api.post("/files/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data.id;
    }

    async function save() {
        if (!audioBlob && !hasExisting && !photoFile && !caption.trim()) {
            toast.error("Record a welcome first, or add a photo / caption.");
            return;
        }
        setBusy(true);
        try {
            const payload = {};
            if (audioBlob) {
                payload.welcome_audio_file_id = await uploadBlob(
                    audioBlob, "welcome.webm", audioBlob.type || "audio/webm"
                );
            }
            if (photoFile) {
                payload.welcome_photo_file_id = await uploadBlob(
                    photoFile, photoFile.name, photoFile.type
                );
            }
            const trimmed = caption.trim();
            if (trimmed) payload.welcome_caption = trimmed;
            const { data } = await api.post(`/profiles/${profileId}/welcome`, payload);
            toast.success("Custom welcome saved.");
            setAudioBlob(null);
            setAudioPreviewUrl(null);
            setPhotoFile(null);
            setPhotoPreviewUrl(null);
            onSaved && onSaved(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save the welcome.");
        } finally {
            setBusy(false);
        }
    }

    async function clearWelcome() {
        if (!hasExisting) return;
        if (!confirm("Remove the custom welcome and restore the default opening?")) return;
        setBusy(true);
        try {
            await api.post(`/profiles/${profileId}/welcome`, {
                welcome_audio_file_id: null, welcome_photo_file_id: null, welcome_caption: null,
            });
            toast.success("Custom welcome removed.");
            onSaved && onSaved({ ...profile, welcome_audio_file_id: null, welcome_photo_file_id: null, welcome_caption: null });
        } catch (err) {
            toast.error("Could not remove the welcome.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <section
            className="bg-white rounded-3xl border border-[#E5E1D8]/70 shadow-rem-soft p-7 sm:p-9 mb-12"
            data-testid="welcome-uploader"
        >
            <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                    <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-[#6B7DB8] font-semibold">
                        Custom welcome · 30 seconds
                    </p>
                    <h3 className="font-serif text-2xl text-[#2D2B2A] mt-1 leading-tight">
                        Record your family's own opening
                    </h3>
                    <p className="mt-2 text-sm text-[#5C5A59] leading-relaxed max-w-2xl">
                        Replace the default AI welcome with up to 30 seconds of your own voice — and add a favourite photo and a short caption. Plays first, every session.
                    </p>
                </div>
                {hasExisting && (
                    <button
                        onClick={clearWelcome}
                        disabled={busy}
                        className="text-xs text-[#8A8786] hover:text-red-600 inline-flex items-center gap-1.5 transition-colors flex-shrink-0"
                        data-testid="welcome-clear"
                    >
                        <Trash size={14} /> Remove current
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Voice block */}
                <div className="bg-[#F4F6FB] rounded-2xl p-5">
                    <p className="text-xs uppercase tracking-wider text-[#4A5A95] font-semibold mb-3">Voice (max 30s)</p>
                    {!recording && !audioPreviewUrl && (
                        <button
                            onClick={startRecording}
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all"
                            data-testid="welcome-record-start"
                        >
                            <Microphone size={16} weight="fill" /> Start recording
                        </button>
                    )}
                    {recording && (
                        <button
                            onClick={stopRecording}
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all animate-pulse"
                            data-testid="welcome-record-stop"
                        >
                            <Stop size={16} weight="fill" /> Stop · {seconds}s / {MAX_SECONDS}s
                        </button>
                    )}
                    {audioPreviewUrl && !recording && (
                        <div className="space-y-2">
                            <audio controls src={audioPreviewUrl} className="w-full" data-testid="welcome-audio-preview" />
                            <button
                                onClick={() => { setAudioBlob(null); setAudioPreviewUrl(null); }}
                                className="text-xs text-[#8A8786] hover:text-[#4A5A95] underline"
                                data-testid="welcome-audio-retake"
                            >
                                Re-record
                            </button>
                        </div>
                    )}
                    {hasExisting && !audioPreviewUrl && (
                        <p className="mt-3 text-xs text-[#6B7DB8] inline-flex items-center gap-1.5" data-testid="welcome-current-indicator">
                            <CheckCircle size={14} weight="fill" /> A custom welcome is already saved.
                        </p>
                    )}
                </div>

                {/* Photo + caption block */}
                <div className="bg-[#FBF1F2] rounded-2xl p-5">
                    <p className="text-xs uppercase tracking-wider text-[#A85D62] font-semibold mb-3">Opening photo (optional)</p>
                    <label
                        htmlFor="welcome-photo-input"
                        className="block cursor-pointer mb-3"
                        data-testid="welcome-photo-label"
                    >
                        {photoPreviewUrl ? (
                            <img src={photoPreviewUrl} alt="Welcome preview" className="w-full aspect-video object-cover rounded-xl border border-[#E89AA1]/40" />
                        ) : (
                            <div className="w-full aspect-video bg-white/60 border-2 border-dashed border-[#E89AA1]/50 rounded-xl flex flex-col items-center justify-center gap-2 text-[#A85D62] hover:bg-white transition-colors">
                                <Camera size={26} weight="light" />
                                <span className="text-xs">Click to choose a photo</span>
                            </div>
                        )}
                    </label>
                    <input
                        id="welcome-photo-input"
                        type="file"
                        accept="image/*"
                        onChange={pickPhoto}
                        className="hidden"
                        data-testid="welcome-photo-input"
                    />
                    <input
                        type="text"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value.slice(0, 140))}
                        placeholder="A short caption (optional)"
                        className="w-full mt-1 px-3 py-2 rounded-full bg-white border border-[#E89AA1]/30 text-sm text-[#3A2C3A] placeholder:text-[#3A2C3A]/40 focus:outline-none focus:border-[#A85D62]"
                        data-testid="welcome-caption-input"
                    />
                </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {hasExisting && (
                        <button
                            type="button"
                            onClick={async () => {
                                setTranslating(true);
                                try {
                                    await api.post(`/profiles/${profileId}/welcome/translate`);
                                    toast.success("Translating into 8 languages — this takes 30–60 seconds.");
                                    const { data } = await api.get(`/profiles/${profileId}`);
                                    onSaved && onSaved(data.profile);
                                } catch (err) {
                                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start translation.");
                                } finally {
                                    setTranslating(false);
                                }
                            }}
                            disabled={busy || translating || isQueuedOrRunning}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F4D068] hover:bg-[#E6B844] disabled:bg-[#8A8786] disabled:cursor-not-allowed text-[#2D2B2A] text-sm font-semibold transition-all"
                            data-testid="welcome-translate"
                        >
                            <Translate size={16} weight="bold" />
                            {isQueuedOrRunning ? `Translating… (${translationStatus})` : `Translate into 8 languages`}
                        </button>
                    )}
                    {translationCount > 0 && !isQueuedOrRunning && (
                        <span className="text-xs text-[#4A5A95] inline-flex items-center gap-1.5" data-testid="welcome-translation-count">
                            <CheckCircle size={14} weight="fill" /> {translationCount}/8 locales ready
                        </span>
                    )}
                    {translationStatus.startsWith("error") && (
                        <span className="text-xs text-red-600" data-testid="welcome-translation-error">
                            Translation hit a snag — try again.
                        </span>
                    )}
                </div>
                <button
                    onClick={save}
                    disabled={busy || (!audioBlob && !photoFile && caption.trim() === (profile.welcome_caption || ""))}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#4A5A95] hover:bg-[#2D3A66] disabled:bg-[#8A8786] disabled:cursor-not-allowed text-white text-sm font-semibold transition-all"
                    data-testid="welcome-save"
                >
                    {busy ? "Saving…" : "Save welcome"}
                </button>
            </div>
        </section>
    );
}
